import type { CombatantBase } from "@lancer-vtt/domain";
import type { GlossaryKey } from "../../glossary/combatGlossary";
import { Term } from "../../glossary/Term";
import { GRIT_BONUS_LABEL, HASE_BONUS_LABEL } from "../../../routes/home/diceBonusRows";
// Type-only, so nothing of `DiceApp.tsx` (or its CSS) is pulled into the combat bundle here. The
// REAL exported type, never a hand-copied shape: a change to DICE.app's entry-point contract must
// break this file's compile rather than drift silently past it.
import type { PreselectBonus } from "../../../routes/home/DiceApp";
import "./unit-sheet.css";

/**
 * The unit sheet's **stat grid** shared by the three PLAY inspector bodies
 * ({@link "./MechInspectorBody".MechInspectorBody}, {@link "./NpcInspectorBody".NpcInspectorBody},
 * {@link "./PilotInspectorBody".PilotInspectorBody}) — hairline cells, each a big value over a small
 * mono label, 8 across when the sheet is 560px or wider and 4 across below (`unit-sheet-redesign`
 * 3.2).
 *
 * Lifted here in `combat-sheet-completeness` Phase 2, when the strip grew from the five cells each
 * body restated inline (EVA / E-DEF / SPD / ARM / SAVE) to eight. Three byte-identical copies of a
 * five-entry array were tolerable; three copies of the read rules below (which fields are optional,
 * what an absent one renders as, which cell is the save TARGET and not the save BONUS) are not.
 *
 * **Every cell reads the FROZEN base and nothing else.** No live roster read, no re-derivation from
 * `pilotData`: the strip must show what the spawn adapter actually froze onto this combatant, so a
 * field nothing populates shows as absent rather than as a confident zero.
 */

/**
 * What a cell renders when its field is **absent from the frozen base** — not zero, *unknown*.
 * Several of the fields below are optional on {@link CombatantBase} and genuinely missing on some
 * kinds (an NPC with no sensors, a mech with no `engineering`), and printing `0` for those would
 * assert a number the engine never froze.
 */
export const STAT_ABSENT = "—";

/**
 * One cell of the strip: a value over a mono label, with the label's glossary term (`combat-glossary`:
 * hover, focus or tap it for the stat's rule) and — when the cell is rollable — the flat bonus its
 * roll button seeds into DICE.app.
 *
 * **`rollBonus` is checked for PRESENCE (`!== undefined`), never truthiness.** A HASE axis nobody
 * has put a point into is a real, pickable **+0**, exactly as it is on PILOT.app's own credential;
 * treating `0` as "not rollable" would silently drop the button off the commonest LL0 cell. The
 * cell's `label` doubles as the roll caption, which is why every rollable cell is captioned with an
 * imported `GRIT_BONUS_LABEL`/`HASE_BONUS_LABEL` rather than a literal — the caption a player sees
 * in DICE.app's SOURCE line is the same string, from the same definition, as the one on the cell.
 */
export type StatCell = { label: string; value: number | string; term?: GlossaryKey; rollBonus?: number };

/** `n` when present, {@link STAT_ABSENT} when the frozen base omits the field. */
function present(n: number | undefined): number | string {
  return n === undefined ? STAT_ABSENT : n;
}

/** The d6 face on a roll button: the one roll glyph, on the combat sheet and the PILOT and MECH pages alike. */
function RollGlyph() {
  return (
    <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      <rect x="0.5" y="0.5" width="11" height="11" fill="none" stroke="currentColor" />
      <circle cx="3.6" cy="3.6" r="1" fill="currentColor" />
      <circle cx="6" cy="6" r="1" fill="currentColor" />
      <circle cx="8.4" cy="8.4" r="1" fill="currentColor" />
    </svg>
  );
}

/**
 * A stat cell's roll icon-button. Its host decides what a press does: the PILOT and MECH pages roll
 * the stat flat in place, COMBAT's sheets load it into the viewer's roller (`shell/railDice.ts`'s
 * `RollEntry`). It is the combat sheet's first roll affordance, and deliberately the
 * same gesture PILOT.app already taught: a small dice button *beside* the number, never the cell
 * itself becoming clickable. On the stat grid it is a 14px glyph in the cell's corner; on the HASE
 * row it sits inline after the value.
 *
 * **A fresh object literal per press, always.** `DiceApp`'s re-seed effect keys on the `preselect`
 * prop's *reference identity*, so a memoized or hoisted preselect object silently no-ops the second
 * press of the same cell — the failure looks like "the button stopped working" and leaves no trace.
 * The contract itself is owned by `pilot-view-click-to-roll`; this is its second consumer, not a
 * fork of it.
 */
export function StatRollButton({ label, bonus, onOpenDice }: { label: string; bonus: number; onOpenDice: (p: PreselectBonus) => void }) {
  return (
    <button
      type="button"
      className="unit-sheet-stat-roll"
      aria-label={`roll ${label}`}
      title={`Roll ${label}`}
      onClick={() => onOpenDice({ label, bonus })}
    >
      <RollGlyph />
    </button>
  );
}

export type DescriptiveField = "evasion" | "edef" | "speed" | "armor" | "size" | "sensors" | "saveTarget";

// The glossary's Save Target line says SAVE TGT is not this unit's own save bonus.
const DESCRIPTIVE_CELLS: readonly { label: string; field: DescriptiveField; term: GlossaryKey }[] = [
  { label: "EVA", field: "evasion", term: "stat:evasion" },
  { label: "E-DEF", field: "edef", term: "stat:edef" },
  { label: "SPD", field: "speed", term: "stat:speed" },
  { label: "ARM", field: "armor", term: "stat:armor" },
  { label: "SIZE", field: "size", term: "stat:size" },
  { label: "SENSORS", field: "sensors", term: "stat:sensors" },
  { label: "SAVE TGT", field: "saveTarget", term: "stat:save_target" },
];

/** What a masked stat cell reads: the value exists, the viewer may not see it. Unlike {@link STAT_ABSENT}. */
export const STAT_MASKED = "??";

/**
 * The descriptive mini-stats a player or GM eyeballs, in reading order: the four defensive/movement
 * numbers the strip has always carried, then SIZE and SENSORS, then the two roll-facing numbers.
 *
 * **SAVE TARGET reads `base.saveTarget` and ONLY that** — deliberately no `?? base.save` fallback
 * (tracker decision D5). `base.save` is the defender's roll *bonus*; `base.saveTarget` is the DC
 * this combatant imposes. They are different quantities that today happen to hold the same number
 * for a mech (`10 + grit` both ways), and a fallback would silently render the bonus under a TARGET
 * label — exactly the conflation `projects/save-bonus-vs-save-target.md` exists to unpick. If a kind
 * ever reaches this strip without a `saveTarget`, the honest answer is the em-dash.
 *
 * `grit` is opt-in per body: a mech and an on-foot pilot freeze their pilot's GRIT into
 * `base.attackBonus`, but an **NPC's** `attackBonus` is its tier attack bonus and is not grit at all
 * (`spawn/npc.ts` reads `attack_bonus` off the class stats), so labelling it GRIT there would be a
 * lie. Per tracker decision D4 the NPC body passes `grit: false`.
 */
export function descriptiveStatCells(base: Pick<CombatantBase, DescriptiveField | "attackBonus">, opts: { grit: boolean }): StatCell[] {
  const cells: StatCell[] = DESCRIPTIVE_CELLS.map(({ label, field, term }) => ({ label, value: present(base[field]), term }));
  if (opts.grit) {
    cells.push({
      label: GRIT_BONUS_LABEL,
      value: present(base.attackBonus),
      // GRIT goes to attack rolls, HP, SP and the SAVE TARGET — NOT to the mech's own save
      // roll, which uses the relevant HASE axis (`decisions/0163`). The glossary's Grit line says
      // so; do not add "and saves" there.
      term: "stat:grit",
      // Rollable only when the base actually froze one. `undefined` here means no roll button, on
      // the same rule the em-dash follows: a cell whose number the engine never wrote must not
      // offer to roll a confident `0` under its label.
      rollBonus: base.attackBonus,
    });
  }
  return cells;
}

/**
 * The NPC stat grid on a masked hostile (`"class"` or `"loadout"` disclosure): the same cells as
 * {@link descriptiveStatCells} with `grit: false`, each reading {@link STAT_MASKED} unless the masked
 * projection keeps its field. Today that is SIZE alone (`ClassTierBase`); a field the projection
 * starts keeping shows its value here with no change.
 */
export function maskedStatCells(base: Partial<Pick<CombatantBase, DescriptiveField>>): StatCell[] {
  return DESCRIPTIVE_CELLS.map(({ label, field, term }) => ({ label, value: base[field] ?? STAT_MASKED, term }));
}

/**
 * Render a grid of {@link StatCell}s: the cell count across (seven for an NPC, eight for a mech or a
 * pilot) when the sheet is 560px or wider, so the wide sheet keeps one full row with no hole; 4 across
 * below that. The HASE row passes `is-hase` and stays 4 across, one slim line.
 *
 * **`onOpenDice` is required-nullable, mirroring `PilotBuildSheet`'s own `onOpenDice`.** Every
 * caller has to *choose*, at the type level, whether this strip is rollable — `null` renders zero
 * roll buttons and is the honest answer for a strip with nothing to roll (an NPC's stat grid: no
 * pilot, no GRIT; its HASE row does roll). An optional prop would let a new call site forget, and forgetting would ship a
 * silently un-rollable sheet rather than a compile error.
 */
export function StatStrip({
  cells,
  className,
  onOpenDice,
}: {
  cells: StatCell[];
  className?: string;
  onOpenDice: ((preselect: PreselectBonus) => void) | null;
}) {
  return (
    <div
      className={`unit-sheet-stats${className ? ` ${className}` : ""}`}
      style={{ ["--unit-sheet-stat-cols" as string]: cells.length }}
    >
      {cells.map((s) => (
        <div key={s.label} className="unit-sheet-stat">
          <span className="unit-sheet-stat-value">{s.value}</span>
          <span className="unit-sheet-stat-label">{s.term ? <Term k={s.term}>{s.label}</Term> : s.label}</span>
          {/* Presence, not truthiness — see {@link StatCell}. A `rollBonus` of 0 is a real +0 roll. */}
          {onOpenDice && s.rollBonus !== undefined && (
            <StatRollButton label={s.label} bonus={s.rollBonus} onOpenDice={onOpenDice} />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The four-cell **HASE** row — HULL / AGILITY / SYSTEMS / ENGINEERING, the pilot skills the table
 * asked to see from the combat view. A mech's and an NPC's: an NPC's HASE is frozen at spawn
 * (`spawnNpc` freezes all four axes) and is what the GM rolls its saves with. A pilot on foot gets
 * none (see `PilotInspectorBody`).
 *
 * **The labels are imported, never restated.** `HASE_BONUS_LABEL` in
 * `routes/home/diceBonusRows.ts` is the single definition of a HASE roll caption, shared with
 * `pilotBonusRows()` and the PILOT sheet's roll buttons; writing `"HULL"` as a literal
 * here is the drift hole `pilot-view-click-to-roll` step 2.4 closed, and it would let COMBAT.app and
 * PILOT.app disagree about what a bonus is called while both files' own tests stayed green. (The
 * credential's abbreviated badge text — `HUL`/`AGI`/`SYS`/`ENG` — is a deliberately *different*
 * string owned by that file's display chrome; it is not this row's label.)
 *
 * **ENGINEERING is absent on a real spawned mech, and renders as {@link STAT_ABSENT}.**
 * `spawn/mech.ts` freezes `hull`/`agility`/`systems` off the pilot's `mechSkills` but not
 * `engineering` (`CombatantBase.engineering`'s own field doc: "**Mechs never carry it**"). Freezing
 * it is tracker **step 2.3**, held pending an integration-ordering ruling because
 * `save-bonus-vs-save-target` needs the same edit to the same adapter; when that lands this cell
 * fills itself with no change here. Until then it degrades to "unknown", never to a misleading `0`,
 * and it is deliberately NOT back-derived from a live `pilotData` read — the strip shows the frozen
 * base or it shows nothing.
 *
 * **An absent axis is also not ROLLABLE, and that is the same rule rather than a second one.** Each
 * cell's `rollBonus` is just its frozen value, so ENGINEERING — absent until 2.3 — renders the
 * em-dash with no roll button beside it, while the other three get one. Seeding a confident `0` into
 * DICE.app for an axis the engine never froze would be strictly worse than the em-dash: DICE.app
 * treats a preselected `0` as a real pick (presence, not truthiness), so the player would get a
 * SOURCE line reading "BONUS · ENGINEERING" over a number nobody computed. When 2.3 freezes the
 * field, the button appears here with no further change.
 */
export function HaseStatRow({
  base,
  onOpenDice,
}: {
  base: HaseFields;
  onOpenDice: ((preselect: PreselectBonus) => void) | null;
}) {
  return <StatStrip cells={haseStatCells(base)} className="is-hase" onOpenDice={onOpenDice} />;
}

type HaseFields = Pick<CombatantBase, "hull" | "agility" | "systems" | "engineering">;

/** The HASE row's cells, each rollable at its frozen (or, on the build page, computed) value. */
export function haseStatCells(base: HaseFields): StatCell[] {
  return [
    { label: HASE_BONUS_LABEL.hull, value: present(base.hull), rollBonus: base.hull },
    { label: HASE_BONUS_LABEL.agi, value: present(base.agility), rollBonus: base.agility },
    { label: HASE_BONUS_LABEL.sys, value: present(base.systems), rollBonus: base.systems },
    { label: HASE_BONUS_LABEL.eng, value: present(base.engineering), rollBonus: base.engineering },
  ];
}
